/**
 * TRAC-1098 — a request for a person is asked once, not on every reentry.
 *
 * TRAC-790 taught the chat to answer "somebody asked for a person" on a chat that
 * already holds a conversation: the page's "Chat with an Expert" button, pressed after
 * chatting to the agent, puts the connect menu in front of the customer instead of
 * silently handing back the old thread.
 *
 * But the page repeats that request on every opening from the same entry point. Send a
 * message, minimize, reopen, and the chat heard a fresh request each time — "Requesting
 * live chat" and the four doors, stacked on a conversation the customer only stepped
 * away from. Coming back is not asking again: on reentry the chat is in the state it was
 * left in.
 *
 * So a person request is put to the chat only when it is new: the first one, or one
 * that follows an explicit request for the agent. The header's "Connect with a Tire
 * Expert" button is untouched and always there for a customer who does want a person now.
 *
 * Remembered per tab (`sessionStorage`), like the conversation it guards: the chat frame
 * is rebuilt on every tirerack.com navigation, and minimizing on one page then reopening
 * from the same button on the next is the same reentry. Storage that cannot be read or
 * written falls back to this frame's memory, which is still right within one page.
 *
 * Pure: storage is passed in. EmbedPage binds it.
 */
export type OpenIntent = 'human' | 'agent';

/** Returns true when this opening's request should be put to the chat as a person request. */
export type PersonRequestGate = (mode: OpenIntent) => boolean;

export const LAST_OPEN_INTENT_KEY = 'traci_last_open_intent';

type IntentStore = Pick<Storage, 'getItem' | 'setItem'>;

export function createPersonRequestGate(storage?: IntentStore | null): PersonRequestGate {
  let last: OpenIntent | null = null;
  try {
    const stored = storage?.getItem(LAST_OPEN_INTENT_KEY);
    if (stored === 'human' || stored === 'agent') last = stored;
  } catch { /* unreadable storage: this frame's memory only */ }
  return (mode) => {
    const isNewAsk = mode === 'human' && last !== 'human';
    last = mode;
    try { storage?.setItem(LAST_OPEN_INTENT_KEY, mode); } catch { /* memory still holds it */ }
    return isNewAsk;
  };
}
